/**
 * Summary Utilities
 * 
 * This module provides utility functions for generating summary prompts
 * from CRM collections, and short card requests for server-side evidence retrieval.
 * 
 * Key features:
 * - Generates formatted prompts for AI summarization
 * - Formats note data with proper date display, handling both Date objects and ISO strings
 * - Includes related entity information (account, contact, author)
 * - Keeps collection evidence scoped and single-record requests reference-only
 */

import { Note } from '../models/Note';
import type { CardDragToChatEventData } from '../constants/eventConstants';

export function getNotesSummaryPrompt(notes: Note[]): string {

  if (!notes || notes.length === 0) return 'No notes to summarize';

  const noteText = (note: Note) => {
    const date = note.updatedAt instanceof Date ? note.updatedAt : new Date(note.updatedAt);
    const noteData = {
      id: note.id,
      title: note.title,
      content: note.content,
      author: note.author || null,
      accountName: note.accountName || null,
      contactName: note.contactName || null,
      date: date.toLocaleString()
    };
    return JSON.stringify(noteData, null, 2);
  }
  const prompt = `Summarize these CRM notes and suggest supported next steps.\n\n${notes.map(n => noteText(n)).join('\n\n')}`;
  return prompt;
}

/** Cards provide identity and intent; the system prompt owns evidence retrieval and analysis. */
export function getCardAnalysisPrompt(reference: CardDragToChatEventData): string {
  if (!reference || typeof reference !== 'object' || Array.isArray(reference)) {
    throw new Error('Invalid CRM card reference');
  }
  const fields = ['noteId', 'accountId', 'contactId'] as const;
  const keys = fields.filter(key => Object.prototype.hasOwnProperty.call(reference, key));
  if (keys.length !== 1) throw new Error('Invalid CRM card reference');
  const key = keys[0];
  const id = (reference as unknown as Record<string, unknown>)[key];
  if (typeof id !== 'number' || !Number.isSafeInteger(id) || id <= 0) {
    throw new Error('Invalid CRM card reference');
  }
  if (key === 'noteId') return `Analyze CRM note ${id}.`;
  return `Analyze the notes for CRM ${key === 'accountId' ? 'account' : 'contact'} ${id}.`;
}
